import { useState, useCallback, type ReactNode } from 'react'
import { PremiumLoader } from '@/components'
import { LoadingContext } from './loadingContext'

interface LoadingProviderProps {
  children: ReactNode
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})
  const [isGlobalLoading, setIsGlobalLoading] = useState(false)

  const startLoading = useCallback((key?: string) => {
    if (key) {
      setLoadingStates(prev => ({ ...prev, [key]: true }))
    } else {
      setIsGlobalLoading(true)
    }
  }, [])

  const stopLoading = useCallback((key?: string) => {
    if (key) {
      setLoadingStates(prev => ({ ...prev, [key]: false }))
    } else {
      setIsGlobalLoading(false)
    }
  }, [])

  const withLoading = useCallback(async (
    asyncFn: () => Promise<unknown>, 
    key?: string
  ) => {
    try {
      startLoading(key)
      const result = await asyncFn()
      return result
    } finally {
      stopLoading(key)
    }
  }, [startLoading, stopLoading])

  // Check if any loading state is active
  const isLoading = isGlobalLoading || Object.values(loadingStates).some(Boolean)

  const value = {
    isLoading,
    startLoading,
    stopLoading,
    withLoading: withLoading as <T>(asyncFn: () => Promise<T>, key?: string) => Promise<T>
  }

  return (
    <LoadingContext.Provider value={value}>
      <PremiumLoader loading={isLoading} />
      <div className={isLoading ? 'bg-black min-h-screen' : ''}>
        {children}
      </div>
    </LoadingContext.Provider>
  )
}

export default LoadingProvider
